<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span>
        {{ $bct('Verify student') }}
      </span>

      <v-spacer />

      <v-btn
        @click="$emit('cancel')"
        data-testid="close-button"
        icon="close"
        variant="text"
      />
    </v-card-title>

    <v-divider />

    <v-card-text class="pt-6 pb-6">
      <div class="mb-4">
        {{ $bct('Please answer the following question(s) to verify') }}
      </div>
      <div
        v-if="enrollment"
        class="mb-4 bg-super-light-blue pa-3"
      >
        <div class="fs-18 fw-600 mb-1">
          {{ enrollment.name }}
        </div>
        <div class="c-light-black">{{ $bct('DOB') }}: {{ enrollment.meta?.child?.dob }}</div>
      </div>

      <div
        v-if="showCurrentProvider"
        class="mb-6"
      >
        <div class="fs-16 fw-600 mb-2">
          {{ $bct(currentProviderVerificationText) }}
        </div>
        <v-radio-group
          v-model="verificationSettings.current_provider_verified"
          data-testid="current-provider-question"
          hide-details
          inline
        >
          <v-radio
            :label="$bct('Yes')"
            :value="true"
          />
          <v-radio
            :label="$bct('No')"
            :value="false"
          />
        </v-radio-group>
      </div>

      <div
        v-if="showSiblingProvider"
        class="mb-6"
      >
        <div class="fs-16 fw-600 mb-2">
          {{ $bct(siblingProviderVerificationText) }}
        </div>
        <v-radio-group
          v-model="verificationSettings.sibling_provider_verified"
          data-testid="sibling-provider-question"
          hide-details
          inline
        >
          <v-radio
            :label="$bct('Yes')"
            :value="true"
          />
          <v-radio
            :label="$bct('No')"
            :value="false"
          />
        </v-radio-group>
      </div>

      <div
        v-if="showStaffProvider"
        class="mb-6"
      >
        <div class="fs-16 fw-600 mb-2">
          {{ $bct(staffProviderVerificationText) }}
        </div>
        <v-radio-group
          v-model="verificationSettings.staff_provider_verified"
          data-testid="staff-provider-question"
          hide-details
          inline
        >
          <v-radio
            :label="$bct('Yes')"
            :value="true"
          />
          <v-radio
            :label="$bct('No')"
            :value="false"
          />
        </v-radio-group>
      </div>
    </v-card-text>

    <v-divider />

    <v-card-actions class="d-flex justify-center pa-4">
      <v-btn
        @click="$emit('cancel')"
        class="me-3"
        color="red"
        variant="outlined"
      >
        {{ $bct('Cancel') }}
      </v-btn>

      <v-btn
        @click="$emit('verify')"
        :disabled="canVerifySelection"
        :loading="loading"
        color="primary"
        variant="flat"
      >
        {{ $bct('Verify') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
const $bct = inject('translator');

const props = defineProps({
  enrollment: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
  verificationSettings: {
    type: Object,
    required: true,
  },
});

defineEmits(['cancel', 'verify']);

const showCurrentProvider = computed(() => {
  return props.subsidyProgram?.enable_current_provider_selection === true;
});

const showSiblingProvider = computed(() => {
  return props.subsidyProgram?.enable_sibling_provider_selection === true;
});

const showStaffProvider = computed(() => {
  return props.subsidyProgram?.enable_staff_provider_selection === true;
});

const canVerifySelection = computed(() => {
  const keys = [];

  if (showCurrentProvider.value) keys.push('current_provider_verified');
  if (showSiblingProvider.value) keys.push('sibling_provider_verified');
  if (showStaffProvider.value) keys.push('staff_provider_verified');

  const hasNullQuestions = () => keys.some((key) => props.verificationSettings[key] === null);

  const isValidSelection = (key) => {
    const computedFlag = key.replace('_verified', '');
    return (
      props.verificationSettings[key] ||
      (!props.verificationSettings[key] &&
        props.enrollment.meta?.computed_subsidy_flags?.[computedFlag])
    );
  };

  const hasAtLeastOneValidChange = () =>
    keys.some((key) => {
      return props.verificationSettings[key] !== props.enrollment[key] && isValidSelection(key);
    });

  return hasNullQuestions() || !hasAtLeastOneValidChange();
});

const currentProviderVerificationText = computed(() => {
  return (
    props.subsidyProgram?.current_provider_verification_text ||
    'Is the child a current student at this location?'
  );
});

const siblingProviderVerificationText = computed(() => {
  return (
    props.subsidyProgram?.sibling_provider_verification_text ||
    'Does the child have a sibling who attends this location?'
  );
});

const staffProviderVerificationText = computed(() => {
  return (
    props.subsidyProgram?.staff_provider_verification_text ||
    'Does the child have a family member on staff?'
  );
});
</script>
